import { useState, useEffect } from 'react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Trash2, AlertTriangle, XCircle, Archive } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { CompensationRule } from '@/services/compensation/types';

interface CompensationRuleDeletionDialogProps {
  rule: CompensationRule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmDeactivate: () => void;
  onConfirmDelete: () => void;
}

interface DependencyReport {
  hasPayslips: boolean;
  hasAssignmentHistory: boolean;
  assignedEmployees: number;
  payslipCount: number;
  historyCount: number;
}

export function CompensationRuleDeletionDialog({
  rule,
  open,
  onOpenChange,
  onConfirmDeactivate,
  onConfirmDelete
}: CompensationRuleDeletionDialogProps) {
  const [dependencyReport, setDependencyReport] = useState<DependencyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [action, setAction] = useState<'deactivate' | 'delete' | null>(null);

  // Check dependencies when dialog opens
  useEffect(() => {
    if (open && rule) {
      checkDependencies();
      setConfirmationText('');
      setAction(null);
    }
  }, [open, rule]);

  const checkDependencies = async () => {
    if (!rule) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Check for payslips using this rule
      const { data: payslips, error: payslipError } = await supabase
        .from('payslip_line_items')
        .select('id', { count: 'exact' })
        .eq('compensation_rule_id', rule.id);

      if (payslipError) throw payslipError;

      // Check for assignment history
      const { data: history, error: historyError } = await supabase
        .from('compensation_assignment_history')
        .select('id', { count: 'exact' })
        .eq('compensation_rule_id', rule.id);

      if (historyError) throw historyError;

      // Check for active assignments instead of legacy profile assignment
      const { data: assignments, error: assignmentError } = await supabase
        .from('compensation_rule_assignments')
        .select('id', { count: 'exact' })
        .eq('rule_id', rule.id)
        .eq('is_active', true);

      if (assignmentError) throw assignmentError;

      const assignedEmployees = assignments?.length || 0;

      setDependencyReport({
        hasPayslips: (payslips?.length || 0) > 0,
        hasAssignmentHistory: (history?.length || 0) > 0,
        assignedEmployees,
        payslipCount: payslips?.length || 0,
        historyCount: history?.length || 0
      });
    } catch (err) {
      console.error('Error checking dependencies:', err);
      setError('Failed to check rule dependencies');
    } finally {
      setLoading(false);
    }
  };

  const handleActionSelect = (selectedAction: 'deactivate' | 'delete') => {
    setAction(selectedAction);
    setConfirmationText('');
  };

  const handleConfirm = () => {
    if (action === 'deactivate') {
      onConfirmDeactivate();
    } else if (action === 'delete') {
      onConfirmDelete();
    }
    onOpenChange(false);
  };

  const canDelete = dependencyReport && !dependencyReport.hasPayslips && !dependencyReport.hasAssignmentHistory;
  const isFormValid = action && (
    (action === 'deactivate') ||
    (action === 'delete' && confirmationText.toLowerCase() === 'delete')
  );

  if (loading) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking Dependencies
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            Analyzing rule usage and dependencies...
          </AlertDialogDescription>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  if (error) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-4 w-4" />
              Error
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>{error}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Manage Compensation Rule
          </AlertDialogTitle>
          <AlertDialogDescription>
            Choose how you want to handle this compensation rule: <strong>{rule?.rule_name}</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {dependencyReport && (
          <div className="space-y-4">
            {/* Usage Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Rule Usage Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-lg">{dependencyReport.assignedEmployees}</div>
                    <div className="text-muted-foreground">Employees</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-lg">{dependencyReport.payslipCount}</div>
                    <div className="text-muted-foreground">Payslips</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-lg">{dependencyReport.historyCount}</div>
                    <div className="text-muted-foreground">History Records</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Action Selection */}
            <div className="space-y-3">
              <h4 className="font-medium">Choose Action:</h4>
              
              {/* Deactivate Option */}
              <Card 
                className={`cursor-pointer transition-colors ${
                  action === 'deactivate' ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => handleActionSelect('deactivate')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Archive className="h-4 w-4" />
                    Deactivate Rule (Recommended)
                    <Badge variant="outline">Soft Delete</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Hides rule from active use</li>
                    <li>• Preserves all historical data</li>
                    <li>• Can be reactivated later</li>
                    <li>• Safe for rules with existing payslips</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Delete Option */}
              <Card 
                className={`cursor-pointer transition-colors ${
                  !canDelete ? 'opacity-50 cursor-not-allowed' : 
                  action === 'delete' ? 'ring-2 ring-destructive' : ''
                }`}
                onClick={() => canDelete && handleActionSelect('delete')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-destructive" />
                    Permanently Delete Rule
                    <Badge variant="destructive">Hard Delete</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {canDelete ? (
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Completely removes the rule</li>
                      <li>• Cannot be undone</li>
                      <li>• Only available for unused rules</li>
                    </ul>
                  ) : (
                    <div className="text-sm text-destructive space-y-1">
                      <p><strong>Cannot delete:</strong> This rule has dependencies</p>
                      <ul className="ml-4 space-y-1">
                        {dependencyReport.hasPayslips && <li>• Used in {dependencyReport.payslipCount} payslip(s)</li>}
                        {dependencyReport.hasAssignmentHistory && <li>• Has {dependencyReport.historyCount} history record(s)</li>}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Confirmation Input for Delete */}
            {action === 'delete' && canDelete && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Type "delete" to confirm permanent deletion:
                </label>
                <Input
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type 'delete' to confirm"
                  className="max-w-xs"
                />
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isFormValid}
            className={action === 'delete' ? 'bg-destructive hover:bg-destructive/90' : ''}
          >
            {action === 'deactivate' && 'Deactivate Rule'}
            {action === 'delete' && 'Permanently Delete'}
            {!action && 'Select Action'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}